import type * as React from 'react'

import { clsx } from 'clsx'

import {
  CheckIcon,
  DropdownMenu,
  Flex,
  Heading,
  Text,
} from '@buffer-mono/popcorn'
import type { FlexProps } from '@buffer-mono/popcorn'

import { ScheduleOptionDefaultMarker } from './ScheduleOptionDefaultMarker'
import type { ScheduleOption } from './types'
import { SCHEDULE_OPTIONS } from '../../composer-footer/schedule-selector/constants'

import styles from './ScheduleOptionItem.module.css'

type ScheduleOptionItemProps = {
  option: ScheduleOption
  onSelectOption: (option: ScheduleOption, event: Event) => void
  isSelected: boolean
  isDefault: boolean
  isDisabled: boolean
  description?: string
  canSetDefault?: boolean
}

const horizontalGap: FlexProps['gap'] = '2xs'

const NoIcon = (): React.ReactElement => (
  <span style={{ width: 16, height: 16, opacity: 0 }} />
)

export const ScheduleOptionItem: React.FC<ScheduleOptionItemProps> = ({
  option,
  onSelectOption,
  isSelected,
  isDefault,
  isDisabled = false,
  description = SCHEDULE_OPTIONS[option].description,
  canSetDefault = true,
}) => {
  const label = SCHEDULE_OPTIONS[option].label

  const disabledText =
    'Option unavailable. Posting slots required in all selected channels.'

  return (
    <>
      {SCHEDULE_OPTIONS[option].withSeparator && <DropdownMenu.Separator />}
      <DropdownMenu.Item
        disabled={isDisabled}
        className={clsx({
          [styles.scheduleOptionItem]: true,
          [styles.scheduleOptionItemSelected]: isSelected,
          [styles.scheduleOptionItemDisabled]: isDisabled,
        })}
        onSelect={(event: Event): void => onSelectOption(option, event)}
      >
        <Flex direction="column" fullWidth>
          <Flex
            direction="row"
            gap={horizontalGap}
            align="center"
            justify="between"
            fullWidth
          >
            <Flex direction="row" gap={horizontalGap} align="center">
              {isSelected ? <CheckIcon size="small" /> : <NoIcon />}
              <Heading
                size="xsmall"
                as="p"
                className={clsx({
                  [styles.scheduleOptionHeadingSelected]: isSelected,
                })}
              >
                {label}
              </Heading>
            </Flex>
            {canSetDefault && (
              <ScheduleOptionDefaultMarker
                className={clsx({
                  [styles.defaultSelector]: true,
                  [styles.defaultSelectorActive]: isDefault,
                })}
                isDefault={isDefault}
                isDisabled={isDisabled}
                option={option}
              />
            )}
          </Flex>

          {description && (
            <Text
              as="small"
              color={isSelected ? 'brand' : 'subtle'}
              className={styles.scheduleOptionDescription}
            >
              {isDisabled ? disabledText : description}
            </Text>
          )}
        </Flex>
      </DropdownMenu.Item>
    </>
  )
}
